import { useEffect, useMemo, useRef } from 'react';

import { sanitizeText } from '../../utils/sanitizeContent';
import { formatAnalysisDate, getOverallSkinScore } from '../../utils/reportConcernsList';
import { drawSkinGauge } from '../../utils/skinGauge';
import EnvironmentTile from './EnvironmentTile';

const PersonalInfoSection = ({ report, userImage }) => {
  const skinGaugeCanvasRef = useRef(null);
  const user = report?.user || {};
  const environment = report?.environment || null;
  const overallScore = useMemo(() => getOverallSkinScore(report), [report]);
  const analysisDate = useMemo(
    () => formatAnalysisDate(report?.ai_report?.created_at),
    [report?.ai_report?.created_at],
  );

  const userName = sanitizeText(user.name || 'Guest');
  const profileSubtitle = [
    user.gender && sanitizeText(user.gender),
    user.age != null ? `${user.age} yrs` : null,
    user.skin_type && sanitizeText(user.skin_type),
    analysisDate !== '--' ? analysisDate : null,
  ].filter(Boolean).join(' · ');

  useEffect(() => {
    if (overallScore == null) return;
    drawSkinGauge(skinGaugeCanvasRef.current, overallScore);
  }, [overallScore]);

  const hasUserData = Boolean(
    user.name
    || user.gender
    || user.age
    || user.skin_type
    || user.instagram_id
    || overallScore != null
    || userImage
    || environment,
  );

  if (!hasUserData) return null;

  return (
    <div className="skin-routine-user-card" id="section-user-info">
      <div className="skin-routine-user-info">
        {userImage && (
          <div className="skin-routine-user-image">
            <img src={userImage} alt="Your skin analysis" />
          </div>
        )}
        <div className="skin-routine-user-details">
          <h2 className="skin-routine-user-name">{userName}</h2>
          {profileSubtitle && (
            <p className="skin-routine-user-subtitle">{profileSubtitle}</p>
          )}
          {user.instagram_id && (
            <p className="skin-routine-user-subtitle">
              Instagram: {sanitizeText(user.instagram_id)}
            </p>
          )}
        </div>
        {overallScore != null && (
          <div className="skin-routine-score-tile skin-routine-score-tile-inline">
            <div
              className="skin-routine-score-canvas-wrapper"
              role="img"
              aria-label={`Overall skin score ${overallScore} out of 100`}
            >
              <canvas
                ref={skinGaugeCanvasRef}
                className="skin-routine-score-canvas"
                width="400"
                height="320"
              />
              <div className="skin-routine-score-display">
                <div className="skin-routine-score-display-value">{overallScore}</div>
                <div className="skin-routine-score-display-label">Skin Score</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="skin-routine-report-strip">
        <EnvironmentTile environment={environment} />
      </div>
    </div>
  );
};

export default PersonalInfoSection;
